<template>
  <div
    class="grid h-max flex-1 gap-6 max-lg:grid-cols-4 max-lg:gap-4"
    :class="[
      extendedGrid ? 'grid-cols-4' : 'grid-cols-5',
      displayListView ? 'max-sm:grid-cols-1' : 'max-sm:grid-cols-2',
    ]"
  >
    <component
      :is="productWrapper || 'div'"
      v-for="(product, index) in products"
      :key="productListKey(product)"
      v-bind="{ product, position: index + 1, ...productWrapperProps }"
      class="products-gallery__products relative h-full"
    >
      <product-card
        :product="product"
        :media-preload="index && index < 4 ? { fetchPriority: 'high' } : false"
        :list-view="displayListView"
        :disable-product-name-expanded="disableProductNameExpanded"
        :analytics-data="analyticsData"
        @click="onProductClick(product, index)"
      />
    </component>
  </div>
</template>

<script setup lang="ts">
import type { Component, PropType, ConcreteComponent } from "vue";
import type { ProductShort } from "~/types/product";
import { CatalogView } from "~/modules/user-settings/types";
import { productListKey } from "~/modules/catalog/helpers/productListKey";

import type { GtagAnalyticsData } from "~/modules/gtm/composables/useGtmEvents";

const props = defineProps({
  products: {
    type: Array as PropType<ProductShort[]>,
    required: true,
  },
  extendedGrid: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  listView: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  analyticsData: {
    type: Object as PropType<GtagAnalyticsData>,
    default: () => ({}),
  },
  disableProductNameExpanded: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  productWrapper: {
    type: Object as PropType<Component | ConcreteComponent | string>,
    default: undefined,
  },
  productWrapperProps: {
    type: Object as PropType<Record<string, any>>,
    default: () => ({}),
  },
});

const emit = defineEmits<{
  (_event: "onProductClick", _product: ProductShort): void;
}>();

const { catalogViewType } = useUserSettings();
const { viewList, productClick } = useGtmAppHandlers();

const displayListView = computed<boolean>(
  () =>
    (props?.listView ?? catalogViewType.value === CatalogView.list) as boolean,
);

function onProductClick(product: ProductShort, index: number) {
  productClick(product, { ...props.analyticsData, index });
  emit("onProductClick", product);
}

onMounted(() => {
  viewList(
    props.products as ProductShort[],
    props.analyticsData as GtagAnalyticsData,
  );
});
</script>
